import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../config/api';
import { partnerCapacityFromError } from '../utils/partnerPlan';

const CsvImportDialog = ({ open, onClose, tripId, isPartner, onImportComplete, onCapacityLimit }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setError('');
      } else {
        setError('Please select a CSV file');
        setFile(null);
      }
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setUploading(true);
    setError('');
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await api.post(`/trips/${tripId}/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data.summary);
      onImportComplete?.(response.data.summary);
    } catch (err) {
      console.error('CSV import error:', err);
      const capacity = isPartner ? partnerCapacityFromError(err) : null;
      if (capacity && onCapacityLimit) {
        onCapacityLimit(capacity);
        return;
      }
      setError(err.response?.data?.error || 'Failed to import CSV file');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError('');
    setImportResult(null);
    onClose();
  };


  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon color="primary" />
          <Typography variant="h6">Import Members from CSV</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!importResult ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a CSV file with member email addresses. The file should have an 'email' column.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => document.getElementById('csv-file-input').click()}
            >
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                {file ? file.name : 'Click to select CSV file'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CSV files only, max 5MB
              </Typography>
            </Box>

            {file && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Selected file: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              CSV import completed successfully!
            </Alert>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Import Summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip 
                  label={`Total: ${importResult.total}`} 
                  color="default" 
                  variant="outlined" 
                />
                <Chip 
                  label={`Valid: ${importResult.valid}`} 
                  color="success" 
                  variant="outlined" 
                />
                <Chip 
                  label={`New Invites: ${importResult.new}`} 
                  color="primary" 
                  variant="outlined" 
                />
                <Chip 
                  label={`Successful: ${importResult.successful}`} 
                  color="success" 
                />
                {importResult.errors > 0 && (
                  <Chip 
                    label={`Errors: ${importResult.errors}`} 
                    color="error" 
                    variant="outlined" 
                  />
                )}
                {importResult.duplicates > 0 && (
                  <Chip 
                    label={`Duplicates: ${importResult.duplicates}`} 
                    color="warning" 
                    variant="outlined" 
                  />
                )}
                {importResult.alreadyInvited > 0 && (
                  <Chip 
                    label={`Already Invited: ${importResult.alreadyInvited}`} 
                    color="info" 
                    variant="outlined" 
                  />
                )}
                {importResult.skipped > 0 && (
                  <Chip 
                    label={`Skipped (Member Limit): ${importResult.skipped}`} 
                    color="warning" 
                    variant="outlined" 
                  />
                )}
              </Box>
            </Paper>

            {(importResult.details.errors.length > 0 || 
              importResult.details.duplicates.length > 0 || 
              importResult.details.alreadyInvited.length > 0 ||
              importResult.details.failed.length > 0) && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Details
                </Typography>

                {importResult.details.errors.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Invalid Emails ({importResult.details.errors.length})
                    </Typography>
                    <List dense>
                      {importResult.details.errors.map((item, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon>
                            <ErrorIcon color="error" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.email} 
                            secondary={item.error}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {importResult.details.duplicates.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="warning.main" gutterBottom>
                      Duplicates in CSV ({importResult.details.duplicates.length})
                    </Typography>
                    <List dense>
                      {importResult.details.duplicates.map((item, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon>
                            <WarningIcon color="warning" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.email} 
                            secondary={item.error}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {importResult.details.alreadyInvited.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="info.main" gutterBottom>
                      Already Members/Invited ({importResult.details.alreadyInvited.length})
                    </Typography>
                    <List dense>
                      {importResult.details.alreadyInvited.map((item, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon>
                            <InfoIcon color="info" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.email} 
                            secondary={item.error}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {importResult.details.failed.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Failed to Send ({importResult.details.failed.length})
                    </Typography>
                    <List dense>
                      {importResult.details.failed.map((item, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon>
                            <ErrorIcon color="error" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.email} 
                            secondary={item.error}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {uploading && (
          <Box sx={{ width: '100%', mr: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Processing CSV file...
            </Typography>
          </Box>
        )}
        
        <Button onClick={handleClose} disabled={uploading}>
          {importResult ? 'Close' : 'Cancel'}
        </Button>
        
        {!importResult && (
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!file || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Importing...' : 'Import CSV'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CsvImportDialog;
